// Layer 3 (Transformation): glass-poc-run A3 / #448 — Paul's per-role note
// slot on module02_metal_balance.html, our facts. `decisions.md` "Persona
// mapping for the landings" (2026-09-17): Control/Senior Metallurgist ->
// metallurgist card, Ops Superintendent -> superintendent card, Concentrator
// Manager -> the financial (GM) card with the NSR table; every other persona
// falls back to the metallurgist card (brief table row "Persona card").
// Pure -- no React.

import type { Persona } from '@/shared/types';
import type { AccountingPeriod } from '@/versions/v1/schema/metal-accounting';
import {
  closureGapPct,
  closureToneFromGap,
  formatGapPct,
  isPeriodMeasured,
  lossDrillRead,
  recoveryActualPct,
  recoveryTargetPct,
  type PeriodView,
} from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import { GROSS_ERROR_FLAGS } from '@/versions/v1/shared/bunker-hill-accounting-seed';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { ladderLineForCase } from '@/versions/v1/shared/ladder.transform';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import { costPeriod, type CostPeriod } from '@/versions/v1/modules/cost/Scene.transform';
import { formatMoney } from '@/versions/v1/shared/NsrEngine';
import type { VizTone } from '@/versions/v1/modules/_viz';

export type PersonaCardVariant = 'metallurgist' | 'superintendent' | 'gm';

/** decisions.md persona mapping, folded to the three card variants this
 *  route renders (brief: "Other personas see the metallurgist card"). */
export function personaCardVariant(persona: Persona): PersonaCardVariant {
  if (persona === 'Superintendent') return 'superintendent';
  if (persona === 'GM') return 'gm';
  return 'metallurgist';
}

export type PersonaFact = {
  id: string;
  text: string;
  tag?: { label: string; tone: VizTone };
  caseId?: string;
  caseTitle?: string;
};

export type MetallurgistPersonaView = { kind: 'metallurgist'; title: string; facts: readonly PersonaFact[] };
export type SuperintendentPersonaView = { kind: 'superintendent'; title: string; facts: readonly PersonaFact[] };
export type GmPersonaView = {
  kind: 'gm';
  title: string;
  rows: readonly { product: string; tonnes: string; nsr: string; budget: string; variance: string; tone: VizTone }[];
};

export type PersonaCardView = MetallurgistPersonaView | SuperintendentPersonaView | GmPersonaView;

function metallurgistFacts(view: PeriodView): readonly PersonaFact[] {
  if (!isPeriodMeasured(view)) {
    return [{ id: 'fact-unmeasured', text: 'Mass balance — unmeasured this shift' }];
  }

  const facts: PersonaFact[] = [];
  const anchor = GROSS_ERROR_FLAGS.find((flag) => flag.id === 'gef-wit021');
  if (anchor) {
    facts.push({
      id: 'fact-anchor',
      text: 'WIT-021 sole fresh-feed anchor',
      tag: { label: 'no cross-check', tone: 'warn' },
    });
  }
  facts.push({
    id: 'fact-assays',
    text: 'Recovery basis: three-product balance off SA03 · SA04 · SA05 lab assays',
    tag: { label: 'sampled', tone: 'ok' },
  });
  for (const element of ['Zn', 'Pb'] as const) {
    const worst = lossDrillRead(element, view).rows.find((row) => row.caseId && row.caseTitle);
    if (worst?.caseId && worst.caseTitle) {
      facts.push({
        id: `fact-case-${element.toLowerCase()}`,
        text: worst.caseTitle,
        tag: { label: 'open case', tone: 'bad' },
        caseId: worst.caseId,
        caseTitle: worst.caseTitle,
      });
      break;
    }
  }
  return facts;
}

// The TopBar persona picker already names the reader (DESIGN.md § Decided,
// "show, don't tell" — no role-in-title); this card's title names what it
// covers instead ("Recovery & reliability"), not who is looking at it.
function superintendentFacts(view: PeriodView): readonly PersonaFact[] {
  if (!isPeriodMeasured(view)) {
    return [{ id: 'fact-unmeasured', text: 'Recovery and closure — unmeasured this shift' }];
  }

  const facts: PersonaFact[] = [];
  const znActual = recoveryActualPct('Zn', view);
  const pbActual = recoveryActualPct('Pb', view);
  const znTarget = recoveryTargetPct('Zn');
  const pbTarget = recoveryTargetPct('Pb');
  if (znActual !== null && pbActual !== null && znTarget !== null && pbTarget !== null) {
    facts.push({
      id: 'fact-recovery',
      text: `Zn ${znActual.toFixed(1)}% vs target ${znTarget.toFixed(1)}% · Pb ${pbActual.toFixed(1)}% vs target ${pbTarget.toFixed(1)}%`,
      tag: {
        label: znActual >= znTarget && pbActual >= pbTarget ? 'on target' : 'below target',
        tone: znActual >= znTarget && pbActual >= pbTarget ? 'ok' : 'bad',
      },
    });
  }
  const znGap = closureGapPct('sp-zn', view);
  const pbGap = closureGapPct('sp-pb', view);
  const closureMeasured = znGap !== null || pbGap !== null;
  const closureTone =
    !closureMeasured
      ? null
      : closureToneFromGap(pbGap) === 'bad' || closureToneFromGap(znGap) === 'bad'
        ? 'bad'
        : closureToneFromGap(pbGap) === 'warn' || closureToneFromGap(znGap) === 'warn'
          ? 'warn'
          : 'ok';
  const closureLabel =
    closureTone === 'ok'
      ? 'reliable'
      : closureTone === 'warn'
        ? 'watch'
        : closureTone === 'bad'
          ? 'out of tolerance'
          : undefined;
  facts.push({
    id: 'fact-closure',
    text:
      closureMeasured
        ? `Mass balance closure: ${pbGap !== null ? `${formatGapPct(pbGap)} Pb` : '—'} · ${znGap !== null ? `${formatGapPct(znGap)} Zn` : '—'} gap`
        : 'Mass balance closure — unmeasured',
    tag:
      closureLabel && closureTone
        ? { label: closureLabel, tone: closureTone }
        : undefined,
  });
  for (const element of ['Zn', 'Pb'] as const) {
    const worst = lossDrillRead(element, view).rows.find((row) => row.caseId);
    if (worst?.caseId) {
      const caseItem = CASES.find((entry) => entry.id === worst.caseId);
      const ladder = caseItem ? ladderLineForCase(caseItem, DEFAULT_CURSOR) : null;
      if (ladder) {
        facts.push({
          id: `fact-tarp-${element.toLowerCase()}`,
          text: ladder.text,
          tag: { label: 'TARP', tone: ladder.tone },
        });
      }
      break;
    }
  }
  return facts;
}

function accountingToCostPeriod(period: AccountingPeriod): CostPeriod | null {
  if (period === 'shift') return null;
  if (period === 'day') return 'yesterday';
  if (period === 'wtd') return 'week';
  return 'month';
}

/** GM card — cost/seed.ts's `costPeriod` for the selected accounting window
 *  (`yesterday` / `week` / `month`), shared with the GM landing. The cost seed carries one
 *  total budget, not a per-product split, so Budget/Variance render only on
 *  the Total row — honest absence, not an invented split (rule 1). */
function gmView(view: PeriodView): GmPersonaView {
  const costWindow = accountingToCostPeriod(view.period);
  if (!costWindow) {
    return {
      kind: 'gm',
      title: 'Financial summary',
      rows: [
        {
          product: '—',
          tonnes: '—',
          nsr: '—',
          budget: '—',
          variance: '—',
          tone: 'neutral',
        },
      ],
    };
  }

  const cost = costPeriod(costWindow);
  const netDelta = cost.headline.netRevenue.deltaPct;

  const rows: GmPersonaView['rows'][number][] = cost.revenueRows.map((row) => ({
    product: row.product,
    tonnes: row.concentrateTonnes.replace(/\bdmt\b/g, 'dst'),
    nsr: row.netRevenue,
    budget: '—',
    variance: '—',
    tone: 'neutral' as VizTone,
  }));

  rows.push({
    product: 'Total',
    tonnes: `${cost.tonnesMilled.toLocaleString('en-US')} dst · ${cost.dateRange}`,
    nsr: formatMoney(cost.headline.netRevenue.actual),
    budget: formatMoney(cost.headline.netRevenue.budget),
    variance: `${netDelta >= 0 ? '+' : ''}${netDelta.toFixed(1)}%`,
    tone: netDelta >= 0 ? 'ok' : 'bad',
  });

  return { kind: 'gm', title: 'Financial summary', rows };
}

export function personaCardView(variant: PersonaCardVariant, view: PeriodView): PersonaCardView {
  if (variant === 'superintendent') {
    return { kind: 'superintendent', title: 'Recovery & reliability', facts: superintendentFacts(view) };
  }
  if (variant === 'gm') return gmView(view);
  return { kind: 'metallurgist', title: 'Balance basis & open items', facts: metallurgistFacts(view) };
}
